import { EdgeModel } from '@common/models';
import { AppDispatch, RootState } from '@redux/store';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

interface DrawingShapeState {
    drawingEdgeUndoStack: EdgeModel[];
    drawingColorUndoStack: string[];
    drawingShapesUndoStackIndex: number;
    highestLevelUndoStackIndex: number;
    activeDrawingAppliance?: string;
    applianceTriggerToggle: boolean;
    needRedrawCanvas: boolean;
    preventRepricing: boolean;
    skipUndoStackUpdates: boolean;
    pauseUndoStackUpdatesForSummary: boolean;
    clearedShapeEditNotifications: string[];
}

const initialState = {
    drawingEdgeUndoStack: [],
    drawingColorUndoStack: [],
    drawingShapesUndoStackIndex: 0,
    highestLevelUndoStackIndex: 0,
    activeDrawingAppliance: undefined,
    applianceTriggerToggle: false,
    needRedrawCanvas: false,
    preventRepricing: false,
    skipUndoStackUpdates: false,
    pauseUndoStackUpdatesForSummary: false,
    clearedShapeEditNotifications: [],
} satisfies DrawingShapeState;

const drawingShapeSlice = createSlice({
    name: 'drawingShape',
    initialState: initialState as DrawingShapeState,
    reducers: {
        clearDrawingShapesUndoStack: (state) => {
            state.drawingShapesUndoStackIndex = initialState.drawingShapesUndoStackIndex;
            state.drawingEdgeUndoStack = initialState.drawingEdgeUndoStack;
            state.drawingColorUndoStack = initialState.drawingColorUndoStack;
        },
        // === start drawingShapesUndoStackIndex actions
        incDrawingShapesUndoStackIndex: (state) => {
            state.drawingShapesUndoStackIndex = state.drawingShapesUndoStackIndex
                ? state.drawingShapesUndoStackIndex + 1
                : 1;
        },
        decDrawingShapesUndoStackIndex: (state) => {
            if (state.drawingShapesUndoStackIndex) {
                state.drawingShapesUndoStackIndex = state.drawingShapesUndoStackIndex - 1;
            }
        },
        resetDrawingShapesUndoStackIndex: (state) => {
            state.drawingShapesUndoStackIndex = initialState.drawingShapesUndoStackIndex;
        },
        setDrawingShapesUndoStackIndex: (state, action: PayloadAction<number>) => {
            state.drawingShapesUndoStackIndex = action.payload;
        },
        // === end drawingShapesUndoStackIndex actions

        // === start highestLevelUndoStackIndex
        setHighestLevelUndoStackIndex: (state, action: PayloadAction<number>) => {
            state.highestLevelUndoStackIndex = action.payload;
        },
        // === end highestLevelUndoStackIndex

        // === start activeDrawingAppliance
        setActiveDrawingAppliance: (state, action: PayloadAction<string>) => {
            state.activeDrawingAppliance = action.payload;
        },
        resetActiveDrawingAppliance: (state) => {
            state.activeDrawingAppliance = initialState.activeDrawingAppliance;
        },
        // === end activeDrawingAppliance
        // === start applianceTriggerToggle
        toggleApplianceTrigger: (state) => {
            state.applianceTriggerToggle = !state.applianceTriggerToggle;
        },
        // === end applianceTriggerToggle

        // === start needRedrawCanvas
        setNeedRedrawCanvas: (state, action: PayloadAction<boolean>) => {
            state.needRedrawCanvas = action.payload;
        },
        // === end needRedrawCanvas
        // === start preventRepricing
        setPreventRepricing: (state, action: PayloadAction<boolean>) => {
            state.preventRepricing = action.payload;
        },
        // === end preventRepricing
        // === start skipUndoStackUpdates
        setSkipUndoStackUpdates: (state, action: PayloadAction<boolean>) => {
            state.skipUndoStackUpdates = action.payload;
        },
        // === end skipUndoStackUpdates
        // === start pauseUndoStackUpdatesForSummary
        setPauseUndoStackUpdatesForSummary: (state, action: PayloadAction<boolean>) => {
            state.pauseUndoStackUpdatesForSummary = action.payload;
        },
        // === end pauseUndoStackUpdatesForSummary
        setDrawingEdgeUndoStack: (state, action: PayloadAction<EdgeModel[]>) => {
            state.drawingEdgeUndoStack = action.payload;
        },
        setDrawingColorUndoStack: (state, action: PayloadAction<string[]>) => {
            state.drawingColorUndoStack = action.payload;
        },
        resetClearedShapeEditNotifications: (state) => {
            state.clearedShapeEditNotifications = initialState.clearedShapeEditNotifications;
        },
        addClearedShapeEditNotification: (state, action: PayloadAction<string>) => {
            if (!state.clearedShapeEditNotifications.includes(action.payload)) {
                state.clearedShapeEditNotifications.push(action.payload);
            }
        },
    },
});

export const {
    incDrawingShapesUndoStackIndex,
    clearDrawingShapesUndoStack,
    decDrawingShapesUndoStackIndex,
    resetDrawingShapesUndoStackIndex,
    setDrawingShapesUndoStackIndex,
    setHighestLevelUndoStackIndex,
    setActiveDrawingAppliance,
    resetActiveDrawingAppliance,
    toggleApplianceTrigger,
    setNeedRedrawCanvas,
    setPreventRepricing,
    setSkipUndoStackUpdates,
    setPauseUndoStackUpdatesForSummary,
    setDrawingEdgeUndoStack,
    setDrawingColorUndoStack,
    resetClearedShapeEditNotifications,
    addClearedShapeEditNotification,
} = drawingShapeSlice.actions;

export const selectDrawingShapesUndoStackIndex = ({ drawingShape }: RootState): number =>
    drawingShape.drawingShapesUndoStackIndex;

export const selectHighestLevelUndoStackIndex = ({ drawingShape }: RootState): number =>
    drawingShape.highestLevelUndoStackIndex;

export const selectActiveDrawingAppliance = ({ drawingShape }: RootState): string | undefined =>
    drawingShape.activeDrawingAppliance;

export const selectApplianceTriggerToggle = ({ drawingShape }: RootState): boolean =>
    drawingShape.applianceTriggerToggle;

export const selectNeedRedrawCanvas = ({ drawingShape }: RootState): boolean => drawingShape.needRedrawCanvas;

export const selectPreventRepricing = ({ drawingShape }: RootState): boolean => drawingShape.preventRepricing;

export const selectSkipUndoStackUpdates = ({ drawingShape }: RootState): boolean => drawingShape.skipUndoStackUpdates;

export const selectPauseUndoStackUpdatesForSummary = ({ drawingShape }: RootState): boolean =>
    drawingShape.pauseUndoStackUpdatesForSummary;

export const selectDrawingEdgeUndoStack = ({ drawingShape }: RootState): EdgeModel[] =>
    drawingShape.drawingEdgeUndoStack;

export const selectDrawingColorUndoStack = ({ drawingShape }: RootState): string[] =>
    drawingShape.drawingColorUndoStack;

export const selectClearedShapeEditNotifications = ({ drawingShape }: RootState): string[] =>
    drawingShape.clearedShapeEditNotifications;

export const pushDrawingShapesUndoStack =
    ({
        selectedColorName,
        selectedEdge,
        drawingShapesUndoStackLength,
    }: {
        selectedColorName: string;
        selectedEdge: EdgeModel;
        drawingShapesUndoStackLength: number;
    }) =>
    (dispatch: AppDispatch, getState: () => RootState) => {
        const drawingColorUndoStack = getState().drawingShape.drawingColorUndoStack;
        const drawingEdgeUndoStack = getState().drawingShape.drawingEdgeUndoStack;
        const drawingShapesUndoStackIndex = getState().drawingShape.drawingShapesUndoStackIndex;

        dispatch(
            setDrawingColorUndoStack(
                drawingColorUndoStack.toSpliced(
                    drawingShapesUndoStackIndex,
                    drawingShapesUndoStackLength,
                    selectedColorName
                )
            )
        );
        dispatch(
            setDrawingEdgeUndoStack(
                drawingEdgeUndoStack.toSpliced(drawingShapesUndoStackIndex, drawingShapesUndoStackLength, selectedEdge)
            )
        );
        dispatch(incDrawingShapesUndoStackIndex());
        dispatch(setHighestLevelUndoStackIndex(drawingShapesUndoStackIndex));
    };

export default drawingShapeSlice.reducer;
